import { Injectable } from '@angular/core';
import { Response } from '@angular/http';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/catch';
import 'rxjs/add/observable/throw';
import * as Msal from 'msal';
import { ConfigService, AzureAdConfig } from './configureservice.service';
import { HttpClient } from './httpClient';
import { Constants } from '../constants/constants';

export interface IAuthenticationModel {
    username: string;
    email: string;
    name: string;
    airfreightrole: number;
    role: number;
    firstName: string;
    lastName: string;
    phone: string;
    title: string;
    manager: string;
    department: string;
    password: string;
    jwt: { token: string; expires: number };
}

@Injectable()
export class AuthenticationService {
    private msalInstance: Msal.UserAgentApplication;
    private configData: AzureAdConfig;

    constructor(private http: HttpClient, private configService: ConfigService) { }

    /**
     * Initialize MSAL configuration dynamically from AzureAd.json
     */
    private async initMsal(): Promise<void> {
        if (this.msalInstance) return;

        await this.configService.loadConfig();
        this.configData = this.configService.getConfig();

        const msalConfig: Msal.Configuration = {
            auth: {
                clientId: this.configData.clientId,
                authority: this.configData.authority,
                redirectUri: this.configData.redirectUri,
                navigateToLoginRequestUrl: false   // 👈 important fix for silent renew
            },
            cache: {
                cacheLocation: this.configData.cacheLocation as 'localStorage' | 'sessionStorage',
                storeAuthStateInCookie: true        // 👈 required for IE/Edge and old MSAL
            }
        };

        this.msalInstance = new Msal.UserAgentApplication(msalConfig);

        this.msalInstance.handleRedirectCallback((error, response) => {
            if (error) {
                console.error('Redirect callback error:', error);
            } else if (response) {
                console.log('Redirect callback success:', response);
            }
        });
    }


    /**
     * Performs login using Azure AD popup
     */
    async login(): Promise<IAuthenticationModel> {
        await this.initMsal();

        try {
            const loginResponse = await this.msalInstance.loginPopup({
                scopes: this.configData.scopes
            });

            const account = loginResponse.account;

            let tokenResponse;

            try {
                tokenResponse = await this.msalInstance.acquireTokenSilent({
                    scopes: this.configData.scopes
                });
            } catch (silentError) {
                console.warn('Silent token acquisition failed, using popup instead:', silentError);
                tokenResponse = await this.msalInstance.acquireTokenPopup({
                    scopes: this.configData.scopes
                });
            }

            return this.createAuthModel(account, tokenResponse.accessToken);
        } catch (error) {
            console.error('Authentication failed:', error);
            throw error;
        }
    }


    /**
     * Build local authentication model
     */
    private createAuthModel(account: any, token: string): IAuthenticationModel {
        const name = (account && account.name) ? account.name : "";
        let firstName = "";
        let lastName = "";

        // Split names: supports "Last, First" OR "First Last"
        if (name.includes(",")) {
            const [ln, fn] = name.split(",");
            lastName = ln.trim();
            firstName = fn.trim();
        } else {
            const parts = name.trim().split(" ");
            firstName = parts[0] || "";
            lastName = parts[parts.length - 1] || "";
        }
        return {
            username: account.userName,
            email: account.userName,
            name: account.name,
            airfreightrole: 0,
            role: 0,
            firstName: firstName,
            lastName: lastName,
            phone: '',
            title: '',
            manager: '',
            department: '',
            password: '',
            jwt: { token: token, expires: 86400 }
        };
    }

    /**
     * Checks if user is authenticated
     */
    isAuthenticated(): boolean {
        return this.msalInstance && this.msalInstance.getAccount() != null;
    }

    /**
     * Helper to extract username
     */
    getUsernameFromEmail(email: string): string {
        if (!email) return '';
        return email.split('@')[0];
    }

    /**
     * Call backend API to get user roles
     */
    getUserRoles(email: string): Observable<number> {
        return this.http.get(`${Constants.restUrl}User/GetUserRoles/${email}`, {})
            .map((res: Response) => res.json())
            .catch(this.handleError);
    }

    /**
     * Call backend API to get AirFreight roles
     */
    getAirFreightRoles(email: string): Observable<number> {
        return this.http.get(`${Constants.restUrl}User/GetAirFreightRoles/${email}`, {})
            .map((res: Response) => res.json())
            .catch(this.handleError);
    }

    /**
     * Common error handler
     */
    private handleError(error: any): Observable<any> {
        console.error('An error occurred', error);
        return Observable.throw(error);
    }
    async logout(): Promise<void> {
        try {
            await this.initMsal();
            this.msalInstance.logout();

        } catch (error) {
            console.error('Logout failed:', error);
        }
    }
}
